//* Library Imports
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, Link, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";

//* Component Imports
import { InputGroup } from "react-bootstrap";
import DefaultButton from "../../../component/base/DefaultButton";
import PasswordInput from "../../../component/base/PasswordInput";

//* Schema Imports
import {
  changeRecoveryPasswordSchema,
  type ChangeRecoveryPasswordRequest,
} from "../../../schemas/common/recovery-password/recorvery-password";

//* Hooks Imports
import { useChangeRecoveryPassword } from "../../../hooks/mutations/recovery-password/use-change-recovery-password";

type FormData = ChangeRecoveryPasswordRequest;

const calculatePasswordStrength = (password: string): { 
  strength: 'weak' | 'medium' | 'strong'; 
  score: number;
  color: string;
} => {
  let score = 0;
  
  if (password.length >= 8) score += 2;
  else if (password.length >= 6) score += 1;
  
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;  
  const hasRepeatedChars = (() => {
    for (let i = 0; i < password.length - 2; i++) {
      if (password[i] === password[i + 1] && password[i] === password[i + 2]) {
        return true;
      }
    }
    return false;
  })();
  
  if (/012|123|234|345|456|567|678|789|890/.test(password)) score -= 2;
  if (hasRepeatedChars) score -= 2;
  if (/(password|123456|qwerty|admin|user|senha|test)/i.test(password)) score -= 3;
  
  if (score >= 6) return { strength: 'strong', score, color: 'success' };
  if (score >= 4) return { strength: 'medium', score, color: 'warning' };
  return { strength: 'weak', score, color: 'danger' };
};

export function ChangeRecoveryPassword() {
  const { token: urlToken } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(changeRecoveryPasswordSchema),
    defaultValues: {
      token: urlToken || "",
      password: "",
      password_confirmation: "",
    },
  });
  const changePasswordMutation = useChangeRecoveryPassword();

  const passwordValue = watch("password");
  const confirmPasswordValue = watch("password_confirmation");
  const passwordStrength = passwordValue ? calculatePasswordStrength(passwordValue) : null;
  
  const hasRepeatedChars = (password: string) => {
    for (let i = 0; i < password.length - 2; i++) {
      if (password[i] === password[i + 1] && password[i] === password[i + 2]) {
        return true;
      }
    }
    return false;
  };
  
  
  const onSubmit = async (data: FormData) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!urlToken) {
      setErrorMessage("Token não encontrado.");
      return;
    }

    try {
      const response = await changePasswordMutation.mutateAsync({
        ...data,
        token: urlToken,
      });
      
      if (response.result) {
        setSuccessMessage("Senha alterada com sucesso! Você será redirecionado automaticamente...");
        setTimeout(() => navigate("/dashboard"), 2000);
      }
    } catch (err: any) {
      setErrorMessage(err.message || "Erro ao alterar senha. Tente novamente.");
    }
  };


  // Verificar se token existe na URL
  if (!urlToken) {
    return (
      <div className="text-center">
        <div className="alert alert-danger" role="alert">
          <strong>Erro:</strong> Token não encontrado na URL.
        </div>
        <Link to="/forgot-password" className="btn btn-primary">
          Solicitar Novo Link
        </Link>
      </div>
    );
  }

  return (
    <>
      <h4 className="text-center mb-4">Redefinir Senha</h4>

      <form onSubmit={handleSubmit(onSubmit)}>        
        {/* Nova Senha */}
        <InputGroup className="mb-3">
          <div className="flex fdcolumn flex1 w-100">
            <PasswordInput
              label="Nova Senha"
              placeholder="Digite sua nova senha"
              className="flex1"
              {...register("password")}
              disabled={changePasswordMutation.isPending}
              autoComplete="new-password"
            />
            {errors.password && (
              <span className="text-danger small mt-1">
                {errors.password.message}
              </span>
            )}
          </div>
        </InputGroup>        
        
        {/* Confirmar Senha */}
        <InputGroup className="mb-3">
          <div className="flex fdcolumn flex1 w-100">
            <PasswordInput
              label="Confirmar Senha"
              placeholder="Confirme sua nova senha"
              className="flex1"
              {...register("password_confirmation")}
              disabled={changePasswordMutation.isPending}
              autoComplete="new-password"
            />
            {errors.password_confirmation && (
              <span className="text-danger small mt-1">
                {errors.password_confirmation.message}
              </span>
            )}
          </div>
        </InputGroup>        
        {/* Indicador de força da senha */}
        {passwordValue && (
          <div className="mb-3">
            {/* Barra de força da senha */}
            {passwordStrength && (
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">Força da senha:</small>
                  <small className={`text-${passwordStrength.color} fw-bold`}>
                    {passwordStrength.strength === 'weak' && 'Fraca'}
                    {passwordStrength.strength === 'medium' && 'Média'}
                    {passwordStrength.strength === 'strong' && 'Forte'}
                  </small>
                </div>
                <div className="progress" style={{ height: '4px' }}>
                  <div
                    className={`progress-bar bg-${passwordStrength.color}`}
                    style={{ width: `${Math.max(10, (passwordStrength.score / 6) * 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            {/* Lista de requisitos */}
            <div className="text-muted small mb-2">
              <strong>Requisitos da senha:</strong>
            </div>
            <div className="small">
              <div className={`${passwordValue.length >= 8 ? 'text-success' : 'text-danger'}`}>
                <i className={`fas ${passwordValue.length >= 8 ? 'fa-check' : 'fa-times'} me-1`}></i>
                Pelo menos 8 caracteres
              </div>
              <div className={`${/[a-z]/.test(passwordValue) ? 'text-success' : 'text-danger'}`}>
                <i className={`fas ${/[a-z]/.test(passwordValue) ? 'fa-check' : 'fa-times'} me-1`}></i>
                Uma letra minúscula
              </div>
              <div className={`${/[A-Z]/.test(passwordValue) ? 'text-success' : 'text-danger'}`}>
                <i className={`fas ${/[A-Z]/.test(passwordValue) ? 'fa-check' : 'fa-times'} me-1`}></i>
                Uma letra maiúscula
              </div>
              <div className={`${/[0-9]/.test(passwordValue) ? 'text-success' : 'text-danger'}`}>
                <i className={`fas ${/[0-9]/.test(passwordValue) ? 'fa-check' : 'fa-times'} me-1`}></i>
                Um número
              </div>
              <div className={`${/[^a-zA-Z0-9]/.test(passwordValue) ? 'text-success' : 'text-danger'}`}>
                <i className={`fas ${/[^a-zA-Z0-9]/.test(passwordValue) ? 'fa-check' : 'fa-times'} me-1`}></i>
                Um símbolo especial
              </div>
              {/* Validações negativas */}
              <div className={`${!/012|123|234|345|456|567|678|789|890/.test(passwordValue) ? 'text-success' : 'text-danger'}`}>
                <i className={`fas ${!/012|123|234|345|456|567|678|789|890/.test(passwordValue) ? 'fa-check' : 'fa-times'} me-1`}></i>
                Sem números em sequência
              </div>              <div className={`${!hasRepeatedChars(passwordValue) ? 'text-success' : 'text-danger'}`}>
                <i className={`fas ${!hasRepeatedChars(passwordValue) ? 'fa-check' : 'fa-times'} me-1`}></i>
                Sem caracteres repetidos
              </div>
            </div>
          </div>
        )}{successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}
        {errorMessage && (
          <div className="alert alert-danger">{errorMessage}</div>
        )}

        <DefaultButton
          type="submit"
          bg="primary"
          width="100%"
          height="3rem"
          text="Alterar Senha"
          loadingtext="Alterando..."
          loading={changePasswordMutation.isPending}
          disabled={
            changePasswordMutation.isPending ||
            !passwordValue ||
            !confirmPasswordValue
          }
        />        
        <div className="text-center mt-3">
          <Link
            to="/login"
            className="small fw-bold text-primary text-decoration-none"
          >
            Voltar ao Login
          </Link>
        </div>
      </form>
    </>
  );
}
